import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ArrowForward from '@mui/icons-material/ArrowForward';
import AccessTime from '@mui/icons-material/AccessTime';
import postsNl from '../data/posts/index';
import postsEn from '../data/posts/index.en';
import { useLanguage } from '../context/LanguageContext';
import { useT } from '../translations';

const CATEGORY_FILTERS = [
	{ filter: null },
	{ filter: (p) => ['beleggen', 'belasting', 'fire', 'pensioen', 'box3', 'rente', 'etf', 'broker', 'tax', 'investing', 'pension', 'legislation', 'financial', 'interest', 'beginners', '4% rule'].some(t => p.tags.includes(t)) },
	{ filter: (p) => p.tags.includes('spanje') || p.tags.includes('spain') || p.tags.includes('andalusie') || p.tags.includes('andalusia') || p.tags.includes('balearen') || p.tags.includes('balearic islands') || p.tags.includes('canarische eilanden') || p.tags.includes('canary islands') },
	{ filter: (p) => p.tags.includes('portugal') },
	{ filter: (p) => p.tags.includes('thailand') },
	{ filter: (p) => p.tags.includes('dubai') },
	{ filter: (p) => p.tags.includes('cyprus') },
	{ filter: (p) => p.tags.includes('zwitserland') || p.tags.includes('switzerland') },
	{ filter: (p) => p.tags.includes('singapore') || p.tags.includes('monaco') },
];

const BlogCard = ({ post, index, t }) => {
	const navigate = useNavigate();

	return (
		<Paper
			elevation={0}
			className="animate-fade-up"
			style={{ '--delay': `${index * 80}ms` }}
			onClick={() => navigate(`/blog/${post.slug}`)}
			sx={{
				p: 3,
				borderRadius: 3,
				border: '1px solid',
				borderColor: 'grey.200',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				gap: 1.5,
				cursor: 'pointer',
				transition: 'box-shadow 0.28s ease, transform 0.28s ease',
				'&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
			}}
		>
			<Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
				{post.tags.map((tag) => (
					<Chip key={tag} label={tag} size="small" sx={{ fontSize: 11 }} />
				))}
			</Box>

			<Typography variant="h6" fontWeight="bold" lineHeight={1.3}>
				{post.title}
			</Typography>

			<Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
				{post.description}
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.disabled' }}>
					<AccessTime sx={{ fontSize: 14 }} />
					<Typography variant="caption">{post.readTime} {t('blogReadTime')}</Typography>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
					<Typography variant="caption" fontWeight={600}>
						{t('blogReadMore')}
					</Typography>
					<ArrowForward sx={{ fontSize: 14 }} />
				</Box>
			</Box>
		</Paper>
	);
};

const BlogIndexPage = () => {
	const { language } = useLanguage();
	const t = useT(language);
	const [activeCategory, setActiveCategory] = useState(0);

	const categoryLabels = t('blogCategories');
	const posts = language === 'en' ? postsEn : postsNl;

	const catFilter = CATEGORY_FILTERS[activeCategory];
	const filteredPosts = catFilter.filter ? posts.filter(catFilter.filter) : posts;

	return (
		<Box sx={{ py: 4 }}>
			<SEOHead
				title={t('blogMetaTitle')}
				description={t('blogMetaDesc')}
				canonical="https://www.visualiseermijnpensioen.nl/blog"
				lang={language === 'en' ? 'en' : 'nl'}
			/>
			<Box className="animate-fade-up" sx={{ mb: 3 }}>
				<Typography variant="h4" fontWeight="bold" gutterBottom>
					{t('blogTitle')}
				</Typography>
				<Typography variant="body1" color="text.secondary" sx={{ maxWidth: 580 }}>
					{t('blogSubtitle')}
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
				{categoryLabels.map((label, i) => (
					<Chip
						key={label}
						label={label}
						onClick={() => setActiveCategory(i)}
						color={activeCategory === i ? 'primary' : 'default'}
						variant={activeCategory === i ? 'filled' : 'outlined'}
						sx={{ cursor: 'pointer' }}
					/>
				))}
			</Box>

			<Grid container spacing={3}>
				{filteredPosts.map((post, index) => (
					<Grid key={post.slug} size={{ xs: 12, md: 6 }}>
						<BlogCard post={post} index={index} t={t} />
					</Grid>
				))}
			</Grid>
		</Box>
	);
};

export default BlogIndexPage;
